import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { createPortal, flushSync } from "react-dom";
import { useApp } from "../../context/AppContext";
import feeService from "../../services/feeService";
import courseService from "../../services/courseService";
import { getYearOptions } from "../../utils/courseYears";
import Pagination from "../../components/common/Pagination";
import PrintInstructions from "../../components/common/PrintInstructions";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";

const PAGE_SIZE = 20;
const ADMISSION_CATEGORY_OPTIONS = [
    "Open",
    "OBC",
    "SC",
    "ST",
    "NT-A",
    "NT-B",
    "NT-C",
    "NT-D",
    "VJ",
    "SBC",
    "TFWS",
    "Open Minority Scholarship",
    "EWS",
];

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const PendingBar = ({ feesDecided, pendingBalance, totalPaid }) => {
    const decided = Number(feesDecided || 0);
    if (decided <= 0) return <div style={{ fontSize: 10, color: "var(--text-muted)" }}>0%</div>;
    const paid = totalPaid !== undefined && totalPaid !== null
        ? Number(totalPaid)
        : Math.max(0, decided - Number(pendingBalance || 0));
    const paidPct = Math.min(100, Math.max(0, Math.round((paid / decided) * 100)));

    return (
        <div style={{ width: 90 }}>
            <div style={{ height: 6, borderRadius: 99, background: "var(--border, #e2e8f0)", overflow: "hidden" }}>
                <div style={{
                    height: "100%",
                    width: `${paidPct}%`,
                    background: paidPct >= 100 ? "#16a34a" : paidPct >= 50 ? "#d97706" : "#dc2626",
                    borderRadius: 99,
                    transition: "width 0.3s ease",
                }} />
            </div>
            <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>{paidPct}% paid</div>
        </div>
    );
};

const cleanWhatsappNumber = (value) => {
    const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
    if (!digits) return "";
    if (digits.length === 10) return `91${digits}`;
    if (digits.length === 12 && digits.startsWith("91")) return digits;
    return digits;
};

const buildReminderMessage = (student, collegeName = "Central India Group of Institutions") => {
    const pendingAmount = Number(
        student?.pendingBalance ??
        student?.pendingAmount ??
        (Number(student?.feesDecided || 0) - Number(student?.totalPaid || 0)) ??
        0
    ).toLocaleString("en-IN");
    return [
        `Dear ${student?.fullName || "Student"},`,
        "",
        `This is a gentle reminder that your outstanding balance fee is Rs. ${pendingAmount}.`,
        "Kindly pay the pending fee at the earliest to avoid any inconvenience.",
        "If you have already completed the payment, please ignore this message.",
        "",
        "Thank you,",
        collegeName,
    ].join("\n");
};

export default function PendingFees() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { academicYear, role, selectedCollegeId, collegeData, collegeName } = useApp();
    const feeBasePath = role === "clerk" ? "/clerk/fees" : role === "accountant" ? "/accountant/fees" : "/fees";

    const [students, setStudents] = useState([]);
    const [courses, setCourses] = useState([]);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    // Filters
    const [filterGender, setFilterGender] = useState("");
    const [filterCourse, setFilterCourse] = useState(() => searchParams.get("course") || "");
    const [filterCourseYear, setFilterCourseYear] = useState(() => searchParams.get("courseYear") || "");
    const [filterCategory, setFilterCategory] = useState("");

    // Print state
    const [printStudents, setPrintStudents] = useState([]);
    const [loadingPrint, setLoadingPrint] = useState(false);
    const [showPrint, setShowPrint] = useState(false);

    // ── Fetch courses from courseService ─────────────────────────────────────
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const params = { isActive: true };
                if (selectedCollegeId && selectedCollegeId !== "all") {
                    params.college = selectedCollegeId;
                }
                const res = await courseService.getAll(params);
                const courseList = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
                setCourses(courseList);
            } catch (err) {
                console.error("Failed to fetch courses:", err);
            }
        };
        fetchCourses();
    }, [selectedCollegeId]);

    // Derived year options based on selected course
    const selectedCourse = courses.find((c) => (c.shortName || c.courseName) === filterCourse);
    const yearOptions = selectedCourse ? getYearOptions(selectedCourse) : [];

    const handleCourseChange = (e) => {
        setFilterCourse(e.target.value);
        setFilterCourseYear(""); // Reset year when course changes
    };

    const fetchPending = useCallback(async () => {
        if (!academicYear?._id) { setLoading(false); return; }
        setLoading(true);
        try {
            const params = {
                academicYear: academicYear._id,
                search,
                page,
                limit: PAGE_SIZE,
                gender: filterGender,
                courseAdmitted: filterCourse,
                courseYear: filterCourseYear,
                admissionCategory: filterCategory,
            };
            if (selectedCollegeId && selectedCollegeId !== "all") {
                params.college = selectedCollegeId;
            }
            const res = await feeService.getPending(params);
            setStudents(res.data);
            setSummary(res.summary);
            setPagination(res.pagination);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [academicYear, selectedCollegeId, search, page, filterGender, filterCourse, filterCourseYear, filterCategory]);

    useEffect(() => { fetchPending(); }, [fetchPending]);
    useEffect(() => { setPage(1); }, [search, academicYear]);
    useEffect(() => { setPage(1); }, [filterGender, filterCourse, filterCourseYear, filterCategory]);

    const handlePrint = async () => {
        if (!academicYear?._id) return;

        setLoadingPrint(true);
        try {
            const params = {
                academicYear: academicYear._id,
                search,
                gender: filterGender,
                courseAdmitted: filterCourse,
                courseYear: filterCourseYear,
                admissionCategory: filterCategory,
                limit: 9999,
                page: 1,
            };
            if (selectedCollegeId && selectedCollegeId !== "all") {
                params.college = selectedCollegeId;
            }
            const res = await feeService.getPending(params);

            flushSync(() => {
                setPrintStudents(res.data);
                setShowPrint(true);
            });

            setTimeout(() => window.print(), 150);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoadingPrint(false);
        }
    };

    useEffect(() => {
        const handleAfterPrint = () => setShowPrint(false);
        window.addEventListener("afterprint", handleAfterPrint);
        return () => window.removeEventListener("afterprint", handleAfterPrint);
    }, []);

    const handleReminder = (student) => {
        const preferredNumber = student?.mobile || student?.fatherMobile;
        const cleanedNumber = cleanWhatsappNumber(preferredNumber);

        if (!cleanedNumber) {
            toast.error("No student or father's mobile number is available for this record.");
            return;
        }

        const message = buildReminderMessage(student, collegeName);
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${cleanedNumber}?text=${encodedMessage}`;

        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
        toast.success(
            student?.mobile
                ? `Opening WhatsApp reminder for ${student.fullName}.`
                : `Opening WhatsApp reminder on father's mobile for ${student.fullName}.`,
        );
    };

    const filteredStudents = students;
    const filteredPrintStudents = printStudents;
    const printTotalPending = filteredPrintStudents.reduce((sum, s) => {
        const decided = Number(s.feesDecided || 0);
        const paid = Number(s.totalPaid || 0);
        const pending = Number(s.pendingBalance ?? s.pendingAmount ?? Math.max(0, decided - paid) ?? 0);
        return sum + pending;
    }, 0);
    const hasActiveFilters = Boolean(search || filterGender || filterCourse || filterCourseYear || filterCategory);

    const printReport = showPrint ? createPortal(
        <div className="pending-fees-print-host">
            <div className="print-area pending-fees-print">
                <style>{`
                    @media print {
                        html, body {
                            height: auto !important;
                            overflow: visible !important;
                            margin: 10px !important;
                            padding: 0 !important;
                        }
                        body > *:not(.pending-fees-print-host) {
                            display: none !important;
                        }
                        .pending-fees-print-host {
                            display: block !important;
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                        .print-area.pending-fees-print,
                        .print-area.pending-fees-print * {
                            visibility: visible !important;
                        }
                        .print-area.pending-fees-print {
                            display: block !important;
                            position: static !important;
                            width: 100% !important;
                            min-height: 0 !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            overflow: visible !important;
                            background: #fff !important;
                            box-sizing: border-box !important;
                        }
                        .print-area.pending-fees-print table {
                            width: 100% !important;
                            border-collapse: collapse !important;
                            page-break-inside: auto;
                        }
                        .print-area.pending-fees-print thead {
                            display: table-header-group;
                        }
                        .print-area.pending-fees-print tfoot {
                            display: table-footer-group;
                        }
                        .print-area.pending-fees-print tr,
                        .print-area.pending-fees-print td,
                        .print-area.pending-fees-print th {
                            page-break-inside: avoid;
                        }
                        @page { size: A4 portrait; margin: 0.25in; }
                    }
                `}</style>

                {/* ── Dynamic College Header ── */}
                <CollegePrintHeader
                    layout="banner"
                    logoSize={58}
                    title="PENDING FEES REPORT"
                    subtitle={`Academic Year: ${academicYear?.label || "—"}`}
                    rightContent={
                        <div style={{ textAlign: "right", fontSize: "11px" }}>
                            <div>Total Students: <strong>{filteredPrintStudents.length}</strong></div>
                            <div>Total Pending: <strong style={{ color: "#dc2626" }}>{inr(printTotalPending)}</strong></div>
                        </div>
                    }
                />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, fontSize: "11px", color: "#475569" }}>
                    <div>
                        {filterGender && <span>Gender: <strong>{filterGender}</strong> · </span>}
                        {filterCourse && <span>Course: <strong>{filterCourse}</strong> · </span>}
                        {filterCourseYear && <span>Year: <strong>{filterCourseYear}</strong> · </span>}
                        {filterCategory && <span>Category: <strong>{filterCategory}</strong> · </span>}
                        <span>Generated: {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</span>
                    </div>
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: "Arial, sans-serif" }}>
                    <thead>
                        <tr style={{ background: "#1e293b", color: "#fff" }}>
                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Sr.</th>
                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Student Name</th>
                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Course</th>
                            <th style={{ padding: "6px 8px", textAlign: "left" }}>Gender</th>
                            <th style={{ padding: "6px 8px", textAlign: "right" }}>Fees Decided</th>
                            <th style={{ padding: "6px 8px", textAlign: "right" }}>Paid</th>
                            <th style={{ padding: "6px 8px", textAlign: "right" }}>Pending</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPrintStudents.map((s, i) => {
                            const decided = Number(s.feesDecided || 0);
                            const paid = Number(s.totalPaid || 0);
                            const pending = Number(s.pendingBalance ?? s.pendingAmount ?? Math.max(0, decided - paid) ?? 0);
                            const courseStr = typeof s.course === "object"
                                ? (s.course?.name || s.course?.shortName)
                                : (s.courseAdmitted || s.course || "—");

                            return (
                                <tr key={s._id} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                    <td style={{ padding: "5px 8px", color: "#64748b" }}>{i + 1}</td>
                                    <td style={{ padding: "5px 8px", fontWeight: 500 }}>
                                        {s.fullName}
                                        <div style={{ fontSize: 9.5, color: "#94a3b8" }}>{s.mobile || ""}</div>
                                    </td>
                                    <td style={{ padding: "5px 8px" }}>{courseStr} {s.courseYear}</td>
                                    <td style={{ padding: "5px 8px" }}>{s.gender}</td>
                                    <td style={{ padding: "5px 8px", textAlign: "right" }}>{inr(decided)}</td>
                                    <td style={{ padding: "5px 8px", textAlign: "right", color: "#16a34a" }}>{inr(paid)}</td>
                                    <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: "#dc2626" }}>{inr(pending)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, fontFamily: "Arial, sans-serif", fontSize: 11, pageBreakInside: "avoid" }}>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ height: 35 }} />
                        <div style={{ borderTop: "1px solid #000", paddingTop: 4, minWidth: 100 }}>Prepared By / Accountant</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ height: 35 }} />
                        <div style={{ borderTop: "1px solid #000", paddingTop: 4, minWidth: 120 }}>
                            <div style={{ fontWeight: 700 }}>Principal / Authorized Signatory</div>
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 16, fontSize: 9.5, color: "#94a3b8", textAlign: "center", borderTop: "1px solid #e2e8f0", paddingTop: 8, fontFamily: "Arial, sans-serif" }}>
                    This is a computer-generated official report.
                </div>
            </div>
        </div>,
        document.body,
    ) : null;

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <h1 className="page-title" style={{ margin: 0 }}>Pending Fees</h1>
                        <span style={{
                            fontSize: 12,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: !selectedCollegeId || selectedCollegeId === "all" ? "#f1f5f9" : "#e0f2fe",
                            color: !selectedCollegeId || selectedCollegeId === "all" ? "#475569" : "#0369a1",
                            border: "1px solid " + (!selectedCollegeId || selectedCollegeId === "all" ? "#cbd5e1" : "#bae6fd"),
                        }}>
                            {!selectedCollegeId || selectedCollegeId === "all" ? "🌐 All Campuses" : `🏢 ${collegeData?.name || collegeName}`}
                        </span>
                    </div>
                    <p className="page-subtitle" style={{ marginTop: 4 }}>
                        {academicYear
                            ? `Students with outstanding balance — ${academicYear.label}`
                            : "No academic year selected"}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate(feeBasePath)}>← Back</button>
                    <button
                        className="btn btn-success no-print"
                        onClick={handlePrint}
                        disabled={loadingPrint || !academicYear?._id}
                    >
                        {loadingPrint ? "Preparing…" : "🖨 Print Report"}
                    </button>
                </div>
            </div>

            {!academicYear ? (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", fontSize: 13, color: "#92400e" }}>
                    ⚠️ Select an academic year from the Dashboard.
                </div>
            ) : (
                <>
                    {summary && !loading && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
                            <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "4px solid #dc2626" }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: "#dc2626" }}>
                                    {summary.totalStudentsWithPending}
                                </div>
                                <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                                    Students with pending dues
                                </div>
                            </div>
                            <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "4px solid #d97706" }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: "#d97706" }}>
                                    {inr(summary.totalPendingAmount)}
                                </div>
                                <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                                    Total outstanding amount
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="card">
                        <div className="no-print" style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)" }}>
                            <PrintInstructions />
                        </div>

                        <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
                            <div className="search-bar">
                                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "#9ca3af", flexShrink: 0 }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    placeholder="Search by name or enrollment…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>

                            <select className="form-control" style={{ width: 120, fontSize: 13 }}
                                value={filterGender} onChange={(e) => setFilterGender(e.target.value)}>
                                <option value="">All Genders</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>

                            <select className="form-control" style={{ width: 175, fontSize: 13 }}
                                value={filterCourse} onChange={handleCourseChange}>
                                <option value="">All Courses</option>
                                {courses.map((c) => (
                                    <option key={c._id || c.shortName} value={c.shortName}>
                                        {c.shortName}
                                    </option>
                                ))}
                            </select>

                            <select
                                className="form-control"
                                style={{ width: 150, fontSize: 13 }}
                                value={filterCourseYear}
                                onChange={(e) => setFilterCourseYear(e.target.value)}
                                disabled={!filterCourse}
                            >
                                <option value="">{filterCourse ? "All Course Years" : "Select Course First"}</option>
                                {yearOptions.map((y) => (
                                    <option key={y.yearNumber} value={y.label}>
                                        {y.label}
                                    </option>
                                ))}
                            </select>

                            <select className="form-control" style={{ width: 150, fontSize: 13 }}
                                value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                                <option value="">All Categories</option>
                                {ADMISSION_CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>

                            {hasActiveFilters && (
                                <button className="btn btn-outline btn-sm"
                                    onClick={() => {
                                        setSearch("");
                                        setFilterGender("");
                                        setFilterCourse("");
                                        setFilterCourseYear("");
                                        setFilterCategory("");
                                    }}>
                                    ✕ Clear
                                </button>
                            )}

                            {hasActiveFilters && (
                                <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: "auto" }}>
                                    Showing {filteredStudents.length} of {pagination?.total ?? filteredStudents.length} filtered record(s)
                                </span>
                            )}
                        </div>

                        {loading ? (
                            <div className="loading-center"><div className="spinner" />Loading pending fees…</div>
                        ) : filteredStudents.length === 0 ? (
                            <div className="empty-state">
                                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p>
                                    {search || filterGender || filterCourse || filterCourseYear || filterCategory
                                        ? "No students match your filters."
                                        : "🎉 No pending fees! All students are cleared."}
                                </p>
                            </div>
                        ) : (
                            <div className="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Student</th>
                                            <th>Course</th>
                                            <th>Fees Decided</th>
                                            <th>Paid</th>
                                            <th>Pending</th>
                                            <th>Progress</th>
                                            <th style={{ textAlign: "right" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredStudents.map((s, i) => {
                                            const decided = Number(s.feesDecided || 0);
                                            const paid = Number(s.totalPaid || 0);
                                            const pending = Number(s.pendingBalance ?? s.pendingAmount ?? Math.max(0, decided - paid) ?? 0);
                                            const courseStr = typeof s.course === "object"
                                                ? (s.course?.name || s.course?.shortName)
                                                : (s.courseAdmitted || s.course || "—");

                                            return (
                                                <tr key={s._id}>
                                                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                        {(page - 1) * (pagination?.limit || PAGE_SIZE) + i + 1}
                                                    </td>
                                                    <td>
                                                        <div style={{ fontWeight: 500 }}>{s.fullName}</div>
                                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                            {s.rollNo ? `Roll: ${s.rollNo} · ` : ""}{s.enrollmentNo} {s.gender ? `· ${s.gender}` : ""}
                                                        </div>
                                                    </td>
                                                    <td style={{ fontSize: 13 }}>
                                                        <div>{courseStr}</div>
                                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{s.courseYear}</div>
                                                    </td>
                                                    <td style={{ fontWeight: 500 }}>{inr(decided)}</td>
                                                    <td style={{ color: "#16a34a", fontWeight: 500 }}>{inr(paid)}</td>
                                                    <td style={{ color: "#dc2626", fontWeight: 700 }}>{inr(pending)}</td>
                                                    <td>
                                                        <PendingBar feesDecided={decided} pendingBalance={pending} totalPaid={paid} />
                                                    </td>
                                                    <td>
                                                        <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                            <button
                                                                className="btn btn-success btn-sm"
                                                                onClick={() => handleReminder(s)}
                                                            >
                                                                Remind
                                                            </button>
                                                            <button className="btn btn-primary btn-sm"
                                                                onClick={() => navigate(`${feeBasePath}/new`, { state: { studentId: s._id } })}>
                                                                + Receipt
                                                            </button>
                                                            <button className="btn btn-outline btn-sm"
                                                                onClick={() => navigate(`${feeBasePath}/student/${s._id}`)}>
                                                                View
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <Pagination pagination={pagination} onPageChange={setPage} />
                    </div>

                    {printReport}
                </>
            )}
        </div>
    );
}